import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { AngleDraftTarget, Concept, ConceptScope, DraftGrade, EstimateFit, GapDraftInput } from "@/types/concept";

export interface AngleDraft {
  label: string;
  question: string;
  search_terms: string;
  abstract_source?: "s2" | "openalex" | "tldr" | null;
  chat_messages_this_month?: number;
}

export interface AnchorPaper {
  title: string;
  year?: number | null;
  abstract?: string | null;
  tldr?: string | null;
  doi?: string | null;
}

/** Guided setup 1: draft an angle for inline review. Persists nothing. */
export function useDraftAngle() {
  return useMutation({
    mutationFn: (body: { name?: string; text?: string; paper?: AnchorPaper; gap?: GapDraftInput }) =>
      apiClient.post("api/concepts/draft-angle", { json: body, timeout: 120_000 }).json<AngleDraft>(),
  });
}

/** Guided setup 2. Optimistic per house rule 8. */
export function useSaveScope(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: ConceptScope) =>
      apiClient.put(`api/concepts/${conceptId}/scope`, { json: scope }).json<Concept>(),
    onMutate: async (scope) => {
      await queryClient.cancelQueries({ queryKey: ["concepts", conceptId] });
      const previous = queryClient.getQueryData<Concept>(["concepts", conceptId]);
      if (previous) queryClient.setQueryData<Concept>(["concepts", conceptId], { ...previous, scope });
      return { previous };
    },
    onError: (_err, _scope, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["concepts", conceptId], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts", conceptId] });
    },
  });
}

export function useCompleteSetup(conceptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`api/concepts/${conceptId}/setup/complete`).json<Concept>(),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["concepts", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"], exact: true });
    },
  });
}

export interface SamplePaper {
  key: string;
  title: string;
  authors?: string[];
  year?: number | null;
  abstract?: string | null;
  journal?: string;
  citations?: number;
  doi?: string | null;
}

export interface SampleGrade {
  key: string;
  score: number;
  justification: string;
}

/** Guided setup 4: grade <=8 preview papers with the real prompt. Saves nothing. */
export function useSampleGrade(conceptId: string, facetId: string | undefined) {
  return useMutation({
    mutationFn: async (papers: SamplePaper[]) => {
      const res = await apiClient
        .post(`api/concepts/${conceptId}/facets/${facetId}/sample-grade`, { json: { papers }, timeout: 120_000 })
        .json<{ results: SampleGrade[] }>();
      return res.results;
    },
  });
}

export type SharpenVerdict = "too_high" | "too_low" | "right";

export interface SharpenMark {
  title: string;
  abstract: string | null;
  score: number;
  justification: string;
  verdict: SharpenVerdict;
}

export interface Sharpened {
  question: string;
  changes: string[];
}

/** ③ "Sharpen & regrade": suggest a sharper question from the marks. Saves nothing. */
export function useSharpenQuestion(conceptId: string, facetId: string | undefined) {
  return useMutation({
    mutationFn: (body: { question: string; marks: SharpenMark[] }) =>
      apiClient
        .post(`api/concepts/${conceptId}/facets/${facetId}/sharpen`, { json: body, timeout: 120_000 })
        .json<Sharpened>(),
  });
}

/** Add-angle ④ (facets 1c-3): grade preview papers against [...active, draft]
 * — overall + this angle. Saves nothing; shares setup's sample-grade limits. */
export function useSampleGradeDraft(conceptId: string) {
  return useMutation({
    mutationFn: async (body: { angle: Pick<AngleDraftTarget, "label" | "question">; papers: SamplePaper[] }) => {
      const res = await apiClient
        .post(`api/concepts/${conceptId}/facets/sample-grade-draft`, { json: body, timeout: 120_000 })
        .json<{ results: DraftGrade[] }>();
      return res.results;
    },
  });
}

/** Board ⑤: grade a sample of the library against a draft angle; persists nothing. */
export function useEstimateFit(conceptId: string) {
  return useMutation({
    mutationFn: (angle: Pick<AngleDraftTarget, "label" | "question">) =>
      apiClient
        .post(`api/concepts/${conceptId}/facets/estimate-fit`, { json: { angle }, timeout: 120_000 })
        .json<EstimateFit>(),
  });
}
